import React, { ForwardedRef } from 'react';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '@mui/material';

import { SpacingKey } from '../../../theme/spacing/index.js';

type UnneededProps = Pick<
  MuiBoxProps,
  | 'sx'
  | 'slot'
  | 'dangerouslySetInnerHTML'
  | 'dir'
  | 'title'
  | 'ref'
  | 'component'
  | 'typography'
  | 'fontFamily'
  | 'fontSize'
  | 'fontStyle'
  | 'fontWeight'
  | 'letterSpacing'
  | 'lineHeight'
  | 'textAlign'
>;
type MuiSpacingProps = Pick<
  MuiBoxProps,
  | 'gap'
  | 'columnGap'
  | 'rowGap'
  | 'm'
  | 'margin'
  | 'mb'
  | 'marginBottom'
  | 'ml'
  | 'marginLeft'
  | 'mr'
  | 'marginRight'
  | 'mt'
  | 'marginTop'
  | 'mx'
  | 'marginX'
  | 'my'
  | 'marginY'
  | 'p'
  | 'padding'
  | 'pb'
  | 'paddingBottom'
  | 'pl'
  | 'paddingLeft'
  | 'pr'
  | 'paddingRight'
  | 'pt'
  | 'paddingTop'
  | 'px'
  | 'paddingX'
  | 'py'
  | 'paddingY'
>;

type NucleusSpacingProps = Partial<Record<keyof MuiSpacingProps, SpacingKey>>;

export interface BoxProps
  extends Omit<MuiBoxProps, keyof UnneededProps | keyof MuiSpacingProps>,
    NucleusSpacingProps {
  /**
   * The tag the Box component will render as.
   * @default 'div'
   */
  as?: MuiBoxProps['component'];

  /**
   * An optional background color of the component.
   */
  backgroundColor?: string;
}

/**
 * Box component is a flexbox container component with different props
 * that can handle a set of styling properties.
 *
 * @see https://mui.com/material-ui/api/box/
 */
export const Box = React.forwardRef(
  (
    {
      as = 'div',
      backgroundColor,
      borderColor,
      borderRadius,
      boxShadow,
      bgcolor,
      color,
      ...rest
    }: BoxProps,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    return (
      <MuiBox
        component={as!}
        ref={ref}
        display="flex"
        boxSizing="border-box"
        sx={{
          borderColor,
          borderRadius,
          boxShadow,
          backgroundColor: bgcolor || backgroundColor,
          color,
        }}
        {...rest}
      />
    );
  },
);

Box.displayName = 'Box';

export default Box;
